import { z } from "zod";
import { artifactRefSchema } from "./context.js";
const visualAssetUrlSchema = z
    .string()
    .url()
    .max(2_000)
    .refine((value) => {
    try {
        const parsed = new URL(value);
        return parsed.protocol === "https:" && !parsed.username && !parsed.password;
    }
    catch {
        return false;
    }
}, "must be an HTTPS URL without embedded credentials");
export const evidenceReviewStatusSchema = z.enum([
    "NEW",
    "IN_REVIEW",
    "APPROVED",
    "REJECTED",
    "DUPLICATE",
]);
/**
 * A deterministic mention found in a source document. Claims are review
 * candidates only: they never represent an executable or published rule.
 */
export const fiscalClaimSchema = z
    .object({
    kind: z.enum(["NCM", "CEST", "UF", "RATE"]),
    family: z.enum(["ICMS", "ICMS_ST", "IPI", "MVA"]).nullable().optional(),
    value: z.string().min(1).max(32),
    excerpt: z.string().min(1).max(240),
    textStart: z.number().int().nonnegative(),
    textEnd: z.number().int().nonnegative(),
    executable: z.literal(false),
})
    .strict();
const evidencePartKindSchema = z.enum(["PDF_PAGES", "HTML_SECTIONS"]);
const evidenceHeadingPathSchema = z.array(z.string().min(1).max(200)).max(8);
function refinePartNumber(value, context) {
    if (value.number > value.count) {
        context.addIssue({
            code: "custom",
            path: ["number"],
            message: "must be between 1 and count",
        });
    }
}
function refinePdfPages(value, context) {
    refinePartNumber(value, context);
    if (value.pageFrom > value.pageTo) {
        context.addIssue({
            code: "custom",
            path: ["pageFrom"],
            message: "must be at most pageTo",
        });
    }
}
/**
 * One stored range of a document that did not fit in a single evidence record.
 * `kind` defaults to `PDF_PAGES` so existing page-range bodies stay valid.
 * HTML parts carry section labels instead of pages.
 */
export const evidencePartSchema = z
    .object({
    parentEvidenceId: z.string().uuid(),
    number: z.number().int().min(1),
    count: z.number().int().min(1).max(16),
    kind: evidencePartKindSchema.default("PDF_PAGES"),
    pageFrom: z.number().int().min(1).optional(),
    pageTo: z.number().int().min(1).optional(),
    sectionFrom: z.string().min(1).max(200).optional(),
    sectionTo: z.string().min(1).max(200).optional(),
    headingPath: evidenceHeadingPathSchema.optional(),
    sectionSplit: z.boolean().optional(),
})
    .strict()
    .superRefine((value, context) => {
    refinePartNumber(value, context);
    if (value.kind === "HTML_SECTIONS") {
        if (value.pageFrom !== undefined || value.pageTo !== undefined) {
            context.addIssue({
                code: "custom",
                path: ["pageFrom"],
                message: "must be omitted for an HTML part",
            });
        }
        if (value.sectionFrom === undefined || value.sectionTo === undefined) {
            context.addIssue({
                code: "custom",
                path: ["sectionFrom"],
                message: "sectionFrom and sectionTo are required",
            });
        }
        return;
    }
    if (value.sectionFrom !== undefined ||
        value.sectionTo !== undefined ||
        value.headingPath !== undefined ||
        value.sectionSplit !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["sectionFrom"],
            message: "must be omitted for a PDF part",
        });
    }
    if (value.pageFrom === undefined || value.pageTo === undefined) {
        context.addIssue({
            code: "custom",
            path: ["pageFrom"],
            message: "pageFrom and pageTo are required",
        });
        return;
    }
    if (value.pageFrom > value.pageTo) {
        context.addIssue({
            code: "custom",
            path: ["pageFrom"],
            message: "must be at most pageTo",
        });
    }
});
/** Same identity as a PDF `evidencePartSchema`, plus the original page count. */
export const evidencePdfPartSchema = z
    .object({
    parentEvidenceId: z.string().uuid(),
    number: z.number().int().min(1),
    count: z.number().int().min(1).max(16),
    pageFrom: z.number().int().min(1),
    pageTo: z.number().int().min(1),
    totalPages: z.number().int().min(1).max(8_000),
})
    .strict()
    .superRefine((value, context) => {
    refinePdfPages(value, context);
    if (value.pageTo > value.totalPages) {
        context.addIssue({
            code: "custom",
            path: ["pageTo"],
            message: "must be within totalPages",
        });
    }
});
/** Structural identity stored beside an HTML evidence part. No page coverage. */
export const evidenceHtmlPartSchema = z
    .object({
    parentEvidenceId: z.string().uuid(),
    number: z.number().int().min(1),
    count: z.number().int().min(1).max(16),
    kind: z.literal("HTML_SECTIONS"),
    sectionFrom: z.string().min(1).max(200),
    sectionTo: z.string().min(1).max(200),
    headingPath: evidenceHeadingPathSchema.optional(),
    sectionSplit: z.boolean().optional(),
})
    .strict()
    .superRefine(refinePartNumber);
/** One sibling in the review list for a split document. Ordered by `number`. */
export const evidencePartSiblingSchema = z
    .object({
    evidenceId: z.string().uuid(),
    number: z.number().int().min(1).max(16),
    kind: evidencePartKindSchema.default("PDF_PAGES"),
    pageFrom: z.number().int().min(1).optional(),
    pageTo: z.number().int().min(1).optional(),
    sectionFrom: z.string().min(1).max(200).optional(),
    sectionTo: z.string().min(1).max(200).optional(),
    reviewStatus: evidenceReviewStatusSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.kind === "HTML_SECTIONS") {
        if (value.pageFrom !== undefined || value.pageTo !== undefined) {
            context.addIssue({
                code: "custom",
                path: ["pageFrom"],
                message: "must be omitted for an HTML part",
            });
        }
        if (value.sectionFrom === undefined || value.sectionTo === undefined) {
            context.addIssue({
                code: "custom",
                path: ["sectionFrom"],
                message: "sectionFrom and sectionTo are required",
            });
        }
        return;
    }
    if (value.sectionFrom !== undefined || value.sectionTo !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["sectionFrom"],
            message: "must be omitted for a PDF part",
        });
    }
    if (value.pageFrom === undefined || value.pageTo === undefined) {
        context.addIssue({
            code: "custom",
            path: ["pageFrom"],
            message: "pageFrom and pageTo are required",
        });
        return;
    }
    if (value.pageFrom > value.pageTo) {
        context.addIssue({
            code: "custom",
            path: ["pageFrom"],
            message: "must be at most pageTo",
        });
    }
});
export const evidenceDocumentSchema = z
    .object({
    id: z.string().uuid(),
    sourceId: z.string().uuid(),
    title: z.string().min(1),
    publisher: z.string().min(1),
    jurisdiction: z.string().min(1),
    publishedAt: z.string().datetime().nullable(),
    fetchedAt: z.string().datetime(),
    canonicalUrl: z.string().url(),
    contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
    rawArtifact: artifactRefSchema,
    normalizedArtifact: artifactRefSchema.nullable(),
    reviewStatus: evidenceReviewStatusSchema,
    confidence: z.number().min(0).max(1).nullable(),
    taxTags: z.array(z.string()),
    /** Publisher metadata; optional so historical evidence remains readable. */
    imageUrl: visualAssetUrlSchema.nullable().optional(),
    imageAlt: z.string().trim().max(240).nullable().optional(),
    sourceLogoUrl: visualAssetUrlSchema.nullable().optional(),
    /** Set only when this document is one part of a larger PDF or HTML act. */
    part: evidencePartSchema.optional(),
})
    .strict();
/** Detail projection used by the operator workbench. */
export const evidenceDetailSchema = evidenceDocumentSchema
    .extend({
    sourceName: z.string().min(1),
    excerpt: z.string(),
    relatedProposalIds: z.array(z.string().uuid()),
    reviewedBy: z.string().nullable(),
    reviewedAt: z.string().datetime().nullable(),
    reviewReason: z.string().nullable(),
    fiscalClaims: z.array(fiscalClaimSchema).max(40).default([]),
    /** Sibling parts of the same document, including this one, ordered by number. */
    parts: z.array(evidencePartSiblingSchema).max(16).optional(),
})
    .passthrough();
//# sourceMappingURL=evidence.js.map